import React from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';
import { AppState } from '../store/combinedReducer';
import AuthProvider from '../authentication/authService';
import { useAuth } from '../hooks/useAuth';
import { Loader } from '@scuf/common';

interface ProtectedRouteProps {
    children: React.ReactNode;
    requiredRoles?: string[];
    requireAcademyAccess?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
    children,
    requiredRoles = [],
    requireAcademyAccess = false
}) => {
    const location = useLocation();
    const { hasAcademyAccess } = useAuth();

    const userData = useSelector((state: AppState) => state.reducer.userInfo);

    // Check if user is authenticated using the same logic as dashboard
    const isAuthenticated = (() => {
        const isLoggedIn = localStorage.getItem("logged_user") === JSON.stringify(true);
        const hasAccessToken = localStorage.getItem("access_token");

        if (!isLoggedIn || !hasAccessToken) return false;

        try {
            const isAuthProviderAuthenticated = AuthProvider().isAuthenticated();
            if (!isAuthProviderAuthenticated) return false;
        } catch (error) {
            return false;
        }

        return true;
    })();

    // If not authenticated, redirect to login
    if (!isAuthenticated) {
        return <Navigate to="/" state={{ from: location }} replace />;
    }

    // Helper function to check specific roles
    const hasRequiredRoles = (roles: string[]): boolean => {
        if (!userData?.roles || !Array.isArray(userData.roles)) return false;
        return roles.some((role: string) =>
            userData.roles.some((userRole: any) => userRole.roleId === role)
        );
    };

    // Check Academy access if required
    if (requireAcademyAccess) {
        const accessGranted = hasAcademyAccess();

        // If permissions are still loading (null), show loading state
        if (accessGranted === null) {
            return (
                <Loader data-testid="academy-access-loader">
                    <div className="placeholder" />
                </Loader>
            );
        }

        // If permissions are loaded but access is denied, redirect to loginFailed
        if (!accessGranted) {
            return <Navigate to="/loginFailed" replace />;
        }
    }

    // Check specific roles if provided
    if (requiredRoles.length > 0 && !hasRequiredRoles(requiredRoles)) {
        return <Navigate to="/loginFailed" replace />;
    }

    return <>{children}</>;
};

export default ProtectedRoute;
